import React, { useState, useEffect } from 'react';
import { Check, Calculator, Minus } from 'lucide-react';

const Comparison = ({ theme }) => {
  const [activeScenario, setActiveScenario] = useState('mid'); // 'small', 'mid', 'large'

  // --- ADDED MOBILE STATE ---
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 1. SCENARIO DATA
  const scenarios = {
    small: {
      title: "Scenario 1: The Single Project",
      desc: "1 Active Site • 40 Daily Workers • High Contractor Turnover",
      tierName: "SiteACT Core",
      siteActCost: "£4,200", 
      competitorName: "Module-Based Apps",
      competitorSub: "(e.g., HereSafe + Permits)",
      competitorCost: "£7,500+", 
      verdict: "Save ~£3k/year",
      explanation: "For single sites, we beat the 'Module' pricers. While basic user apps might look cheaper initially (£50/mo), they lack the Permit Logic and Asset Gating included in our Core plan."
    },
    mid: {
      title: "Scenario 2: The Growing Contractor",
      desc: "3 Active Sites • 150 Daily Workers • Regional Projects",
      tierName: "SiteACT Pro",
      siteActCost: "£10,800", 
      competitorName: "Per-User Platforms",
      competitorSub: "(e.g., iAuditor / SiteDocs)",
      competitorCost: "£21,600+", 
      verdict: "Save 50%",
      explanation: "This is the 'Death Zone' for per-user pricing. As you scale to 3 sites, paying £12 per head destroys your margin. Our Pro Bundle covers all 3 sites for a flat £900/mo."
    },
    large: {
      title: "Scenario 3: The Manufacturer (Factory Network)",
      desc: "10 Factories • 700+ Daily Users • Shutdown Spikes",
      tierName: "SiteACT Enterprise",
      siteActCost: "£23,400", 
      competitorName: "Legacy Enterprise Giants",
      competitorSub: "(e.g., HammerTech / Procore)",
      competitorCost: "£150,000+", 
      verdict: "Save ~85%",
      explanation: "Legacy providers charge huge implementation fees and 'per-site' minimums that punish scale. With SiteACT, you get the same enterprise-grade features (LOTO, Logic Permits, Gate Control) without paying for their expensive sales teams and 6-month setups."
    }
  };

  const current = scenarios[activeScenario];

  return (
    <section style={{ padding: isMobile ? '60px 20px' : '100px 20px', backgroundColor: '#fff', borderTop: '1px solid #f1f5f9' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        
        {/* HEADLINE */}
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <div style={{ 
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            padding: '8px 16px', borderRadius: '30px', background: '#ecfdf5', 
            color: '#059669', fontWeight: '600', fontSize: '0.9rem', marginBottom: '20px'
          }}>
            <Calculator size={16} /> Honest Price Comparison
          </div>
          <h2 style={{ fontSize: isMobile ? '2rem' : '2.5rem', fontWeight: '800', color: theme.secondary, marginBottom: '20px' }}>
            Where we win. <span style={{ color: '#94a3b8' }}>And where we don't.</span>
          </h2>
          <p style={{ fontSize: isMobile ? '1rem' : '1.2rem', color: '#64748b', maxWidth: '750px', margin: '0 auto' }}>
            Most competitors hide their pricing calculators. Here is exactly how we stack up against the market in three common scenarios.
          </p>
        </div>

        {/* SCENARIO TOGGLES */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '40px', flexWrap: 'wrap' }}>
          {['small', 'mid', 'large'].map((key) => (
            <button
              key={key}
              onClick={() => setActiveScenario(key)}
              style={{
                padding: isMobile ? '10px 16px' : '12px 24px',
                borderRadius: '8px',
                border: activeScenario === key ? `2px solid ${theme.primary}` : '1px solid #e2e8f0',
                background: activeScenario === key ? '#eff6ff' : 'white',
                color: activeScenario === key ? theme.primary : '#64748b',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: isMobile ? '0.9rem' : '1rem',
                flexGrow: isMobile ? 1 : 0 // Allows buttons to fill screen evenly on mobile
              }}
            >
              {scenarios[key].title}
            </button>
          ))}
        </div>

        {/* SCENARIO CARD */}
        <div style={{ 
          background: '#0f172a', 
          borderRadius: '24px', 
          padding: isMobile ? '40px 20px' : '50px', 
          color: 'white', 
          marginBottom: '80px', 
          boxShadow: '0 20px 40px -10px rgba(0,0,0,0.3)',
          boxSizing: 'border-box' // Prevents padding from causing horizontal overflow
        }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h3 style={{ fontSize: isMobile ? '1.5rem' : '1.8rem', fontWeight: 'bold', marginBottom: '10px' }}>{current.title}</h3>
            <p style={{ color: '#94a3b8', fontSize: '1rem' }}>{current.desc}</p>
          </div>

          <div style={{ 
            display: 'grid', 
            // Better layout technique: stacks into 1 column on mobile, uses exact 3-column spacing on desktop
            gridTemplateColumns: isMobile ? '1fr' : '1fr auto 1fr', 
            gap: isMobile ? '30px' : '40px', 
            alignItems: 'center' 
          }}>
            
            {/* COMPETITOR SIDE */}
            <div style={{ textAlign: 'center', opacity: 0.8 }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '5px' }}>{current.competitorName}</div>
              <div style={{ fontSize: '0.9rem', marginBottom: '20px' }}>{current.competitorSub}</div>
              <div style={{ fontSize: isMobile ? '2rem' : '2.5rem', fontWeight: 'bold', textDecoration: 'line-through', color: '#ef4444' }}>
                {current.competitorCost}
              </div>
              <div style={{ fontSize: '0.9rem', marginTop: '10px' }}>Annual Cost</div>
            </div>

            {/* VS */}
            <div style={{ textAlign: 'center' }}>
                <div style={{ 
                    width: '50px', height: '50px', background: 'rgba(255,255,255,0.1)', 
                    borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto', fontWeight: 'bold'
                }}>VS</div>
            </div>

            {/* SITEACT SIDE */}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '5px', color: theme.primary }}>{current.tierName}</div>
              <div style={{ fontSize: '0.9rem', marginBottom: '20px', color: '#cbd5e1' }}>Everything Included</div>
              <div style={{ fontSize: isMobile ? '2.8rem' : '3.5rem', fontWeight: '800', color: theme.primary }}>
                {current.siteActCost}
              </div>
              <div style={{ 
                  background: theme.success || '#10b981', color: '#064e3b', 
                  display: 'inline-block', padding: '4px 12px', borderRadius: '4px',
                  fontWeight: 'bold', fontSize: '0.9rem', marginTop: '10px'
              }}>
                {current.verdict}
              </div>
            </div>
          </div>

          <div style={{ marginTop: '40px', paddingTop: '30px', borderTop: '1px solid #1e293b', textAlign: 'center', color: '#cbd5e1', lineHeight: '1.6', maxWidth: '800px', margin: '40px auto 0' }}>
            "{current.explanation}"
          </div>
        </div>

        {/* FEATURE WIN/LOSS MATRIX */}
        <h3 style={{ textAlign: 'center', fontSize: isMobile ? '1.5rem' : '1.8rem', fontWeight: 'bold', marginBottom: '40px', color: theme.secondary }}>
            Feature Showdown
        </h3>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: '30px' 
        }}>
            
            {/* WHERE WE WIN */}
            <div style={{ padding: '30px', border: `1px solid ${theme.primary}`, borderRadius: '16px', background: '#eff6ff', boxSizing: 'border-box' }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.2rem', color: theme.secondary, marginBottom: '20px' }}>
                    <Check size={24} color={theme.primary} style={{ flexShrink: 0 }} /> Where SiteACT Wins
                </h4>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                    {[
                        { title: "Unlimited Users", desc: "We never charge for churn." },
                        { title: "Zero App Download", desc: "Competitors force installs. We use QR." },
                        { title: "Included Modules", desc: "Assets, Permits & Inductions are standard." },
                        { title: "Setup Speed", desc: "Live in 24 hours vs 3 months." }
                    ].map((item, i) => (
                        <li key={i} style={{ marginBottom: '15px' }}>
                            <strong>{item.title}:</strong> <span style={{ color: '#475569' }}>{item.desc}</span>
                        </li>
                    ))}
                </ul>
            </div>

            {/* WHERE THEY WIN (Honesty) */}
            <div style={{ padding: '30px', border: '1px solid #e2e8f0', borderRadius: '16px', background: 'white', boxSizing: 'border-box' }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.2rem', color: '#64748b', marginBottom: '20px' }}>
                    <Minus size={24} style={{ flexShrink: 0 }} /> Where Competitors Win
                </h4>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                    {[
                        { title: "RAMS Builder", desc: "Some apps let you create RAMS. We require uploads." },
                        { title: "Video Hosting", desc: "We don't host 4K induction videos yet." },
                        { title: "Tiny Teams (<10)", desc: "For very small static teams, per-user pricing is cheaper." }
                    ].map((item, i) => (
                        <li key={i} style={{ marginBottom: '15px', color: '#94a3b8' }}>
                            <strong>{item.title}:</strong> {item.desc}
                        </li>
                    ))}
                </ul>
            </div>

        </div>

      </div>
    </section>
  );
};

export default Comparison;